import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  DiceError,
  DiceExpression,
  SheetEvaluator,
  describeRoll,
  isNaturalMax,
  isNaturalMin,
  isRollable,
  javaIsBlank,
  javaStrip,
  optionLabel,
  secureRandom,
  type RollResult,
  type SheetField,
  type SheetTemplate,
} from '@realmery/realm-core';

/**
 * A character sheet laid out from its template, as the desktop's `SheetForm`: each section
 * a titled box of fields in its columns, each field the control its type calls for, derived
 * values worked out again after every change, and a 🎲 beside anything rollable. It knows
 * nothing about saving: it reports each change, so the sheet page and the template editor's
 * preview share it.
 */
export function SheetForm({
  template,
  values,
  onChange,
  onRolled,
}: {
  template: SheetTemplate;
  values: ReadonlyMap<string, string>;
  /** A field's new text; empty clears it back to the template's default. */
  onChange: (key: string, value: string) => void;
  onRolled: (field: SheetField, result: RollResult) => void;
}) {
  const sheet = useMemo(() => new SheetEvaluator(template, values), [template, values]);
  return (
    <div className="sheet-form">
      {template.sections.map((section, i) => (
        <section key={i} className="sheet-section" aria-label={section.title || 'Untitled section'}>
          {section.title && <h2>{section.title}</h2>}
          <div className={`sheet-grid sheet-cols-${section.columns}`}>
            {section.fields.map((field, j) => (
              <Cell key={`${field.key}-${j}`} field={field} sheet={sheet} values={values} onChange={onChange} onRolled={onRolled} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** A field's tooltip: its help, or failing that its label. */
const tip = (field: SheetField) => field.help || field.label;

function Cell({
  field,
  sheet,
  values,
  onChange,
  onRolled,
}: {
  field: SheetField;
  sheet: SheetEvaluator;
  values: ReadonlyMap<string, string>;
  onChange: (key: string, value: string) => void;
  onRolled: (field: SheetField, result: RollResult) => void;
}) {
  const id = useId();
  const key = field.key;
  const problem = field.type === 'NUMBER' || field.type === 'RESOURCE' || field.type === 'DERIVED' ? sheet.problem(key) : null;
  const written = values.get(key) ?? field.defaultValue;
  let control: React.ReactNode;
  switch (field.type) {
    case 'NUMBER':
      control = <TextInput id={id} className="sheet-number" value={written} invalid={problem} title={problem ?? tip(field)} onText={(text) => onChange(key, javaStrip(text))} />;
      break;
    case 'TEXT':
      control = <TextInput id={id} value={written} title={tip(field)} onText={(text) => onChange(key, text)} />;
      break;
    case 'LONG_TEXT':
      control = <TextInput id={id} multiline value={written} title={tip(field)} onText={(text) => onChange(key, text)} />;
      break;
    case 'CHECKBOX':
      return (
        <div className="sheet-field">
          <label className="check" title={tip(field)}>
            <input type="checkbox" checked={sheet.isChecked(key)} onChange={(e) => onChange(key, e.currentTarget.checked ? 'true' : '')} />
            <span>{field.label}</span>
          </label>
          <RollButton field={field} sheet={sheet} onRolled={onRolled} />
        </div>
      );
    case 'CHOICE':
      control = (
        <select id={id} title={tip(field)} value={sheet.choiceLabel(key)} onChange={(e) => onChange(key, e.currentTarget.value)}>
          {field.options.map((option, i) => (
            <option key={i}>{optionLabel(option)}</option>
          ))}
        </select>
      );
      break;
    case 'RESOURCE':
      control = <Resource id={id} field={field} sheet={sheet} values={values} problem={problem} onChange={onChange} />;
      break;
    case 'DERIVED': {
      const value = sheet.number(key);
      control = (
        <output id={id} className={problem ? 'sheet-derived sheet-field-error' : 'sheet-derived'} title={problem ?? `= ${field.formula}`}>
          {value === null ? '—' : String(value)}
        </output>
      );
      break;
    }
    case 'DICE':
      control = (
        <TextInput
          id={id}
          className="sheet-dice"
          value={written}
          invalid={!javaIsBlank(written) && !DiceExpression.isValid(written) ? 'Not a roll yet' : null}
          title={field.help || `${field.label} - a roll, like 2d6 + 3`}
          onText={(text) => onChange(key, javaStrip(text))}
        />
      );
      break;
  }
  return (
    <div className={field.type === 'LONG_TEXT' ? 'sheet-field sheet-wide' : 'sheet-field'}>
      <label htmlFor={id} className="sheet-field-label" title={field.help || undefined}>
        {field.label}
      </label>
      <div className="sheet-control">
        {control}
        <RollButton field={field} sheet={sheet} onRolled={onRolled} />
      </div>
      {problem && field.type !== 'DERIVED' && <span className="visually-hidden">{problem}</span>}
    </div>
  );
}

/** A text box that keeps what's typed while it has focus, and follows the sheet otherwise. */
function TextInput({
  id,
  value,
  onText,
  multiline = false,
  className,
  invalid = null,
  title,
}: {
  id: string;
  value: string;
  onText: (text: string) => void;
  multiline?: boolean;
  className?: string;
  invalid?: string | null;
  title: string;
}) {
  const [text, setText] = useState(value);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value);
  }, [value]);
  const props = {
    id,
    value: text,
    title,
    className: [className, invalid ? 'sheet-field-error' : ''].filter(Boolean).join(' ') || undefined,
    'aria-invalid': invalid ? true : undefined,
    onFocus: () => (focused.current = true),
    onBlur: () => (focused.current = false),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setText(e.currentTarget.value);
      onText(e.currentTarget.value);
    },
  };
  return multiline ? <textarea rows={3} {...props} /> : <input {...props} />;
}

function Resource({
  id,
  field,
  sheet,
  values,
  problem,
  onChange,
}: {
  id: string;
  field: SheetField;
  sheet: SheetEvaluator;
  values: ReadonlyMap<string, string>;
  problem: string | null;
  onChange: (key: string, value: string) => void;
}) {
  const key = field.key;
  const max = sheet.max(key);
  const step = (delta: number) => {
    let next = (sheet.number(key) ?? 0n) + BigInt(delta);
    if (max !== null && next > max) next = max;
    onChange(key, String(next < 0n ? 0n : next));
  };
  return (
    <span className="sheet-resource">
      <button type="button" className="quiet sheet-step" aria-label={`One less ${field.label}`} title={`One less ${field.label}`} onClick={() => step(-1)}>
        −
      </button>
      <TextInput id={id} className="sheet-number" value={sheet.raw(key)} invalid={problem} title={problem ?? tip(field)} onText={(text) => onChange(key, javaStrip(text))} />
      <span aria-hidden="true">/</span>
      {field.formula === '' ? (
        <TextInput
          id={`${id}-max`}
          className="sheet-number"
          value={values.get(`${key}.max`) ?? field.defaultValue}
          title={`${field.label}: maximum`}
          onText={(text) => onChange(`${key}.max`, javaStrip(text))}
        />
      ) : (
        <output className="sheet-derived" title={`Maximum: ${field.formula}`} aria-label={`${field.label}: maximum`}>
          {max === null ? '—' : String(max)}
        </output>
      )}
      <button type="button" className="quiet sheet-step" aria-label={`One more ${field.label}`} title={`One more ${field.label} (up to the maximum)`} onClick={() => step(1)}>
        +
      </button>
    </span>
  );
}

function RollButton({ field, sheet, onRolled }: { field: SheetField; sheet: SheetEvaluator; onRolled: (field: SheetField, result: RollResult) => void }) {
  const [shown, setShown] = useState<{ text: string; title: string; mark: string } | null>(null);
  if (!isRollable(field)) return null;
  function roll() {
    try {
      const result = sheet.roll(field.key, secureRandom);
      const mark = result.groups.some(isNaturalMax) ? ' dice-natural-max' : result.groups.some(isNaturalMin) ? ' dice-natural-min' : '';
      setShown({ text: String(result.total), title: describeRoll(result), mark });
      onRolled(field, result);
    } catch (e) {
      if (!(e instanceof DiceError)) throw e;
      setShown({ text: '!', title: e.message, mark: ' error-text' });
    }
  }
  return (
    <>
      <button type="button" className="quiet sheet-roll-button" aria-label={`Roll ${field.label}`} title={`Roll ${field.type === 'DICE' ? 'this' : field.roll}`} onClick={roll}>
        🎲
      </button>
      {shown && (
        <output className={`sheet-roll-result${shown.mark}`} title={shown.title} aria-label={`${field.label}: ${shown.title}`}>
          {shown.text}
        </output>
      )}
    </>
  );
}
